import { PRINT_GEOMETRY, rowBand } from "../print/CashSheetPrint";
import { num } from "../money";
import type { CashRowView, Doubt, OverDueReason, PhotoView, PrintCheck, RowItem } from "../types";

/**
 * Pure rules of the sheet check screen (V1_SPEC 7.4, 11.6 L2-L4, D11-D14). The server decides; these
 * keep the screen from offering what the server would refuse, and order the rows the same way.
 */

/** Doubts that put a row under "Needs a look" (the rest are chips on the row only). */
export const LOOK_DOUBTS: Doubt[] = ["PAID_TWICE", "OVER_DUE", "EDITED", "PAY_CHANGED", "PHOTO_AFTER_CHECK"];

export const OVER_DUE_REASONS: { code: OverDueReason; label: string }[] = [
  { code: "ADVANCE", label: "Advance" },
  { code: "BONUS", label: "Bonus" },
  { code: "BACK_PAY", label: "Back pay" },
  { code: "WRONG", label: "Wrong" },
];

export const GROUP_MAX = 10;

export const needsLook = (r: CashRowView): boolean => r.doubts.some((d) => LOOK_DOUBTS.includes(d));
export const isSpot = (r: CashRowView, check: Pick<PrintCheck, "spotRowIndexes">): boolean =>
  r.doubts.includes("SPOT_CHECK") || (check.spotRowIndexes || []).includes(r.rowIndex);
export const isOwn = (r: CashRowView): boolean => r.canVerify === "RECORDED_BY_YOU" || r.canVerify === "YOUR_OWN_PAY";

export type Sections = { look: CashRowView[]; spot: CashRowView[]; clean: CashRowView[] };

/**
 * "Needs a look", "Spot checks", "Not changed in the app" (D11). Doubtful rows first, then the blind
 * spot rows, then the rest by row number; own rows stay where they are, with their chip.
 */
export const sectionsOf = (check: Pick<PrintCheck, "rows" | "spotRowIndexes">): Sections => {
  const byIndex = [...check.rows].sort((a, b) => a.rowIndex - b.rowIndex);
  const look = byIndex.filter(needsLook);
  const spot = byIndex.filter((r) => !needsLook(r) && isSpot(r, check));
  const clean = byIndex.filter((r) => !needsLook(r) && !isSpot(r, check));
  return { look, spot, clean };
};

/** The rows in screen order (what ↑ and ↓ walk through). */
export const screenOrder = (s: Sections): CashRowView[] => [...s.look, ...s.spot, ...s.clean];

/** True once the TOTAL is typed and the gap is ₹0, or the checker said the paper's own sum is wrong. */
export const gapCleared = (check: Pick<PrintCheck, "paperTotal" | "gap" | "totalOverridden">): boolean =>
  check.paperTotal !== null && check.paperTotal !== undefined && (check.totalOverridden || num(check.gap) === 0);

/** The gap as a positive amount for "Explain ₹220 first". */
export const gapAmount = (check: Pick<PrintCheck, "gap">): number => Math.abs(num(check.gap));

/** Paid as entered: "" = left blank on the paper (not paid), null = not entered yet. */
export const enteredPaid = (r: CashRowView): string | null => r.paid;

/** A draft is what the checker has typed on the screen and not saved yet. undefined = untouched. */
export type Draft = { paid?: string; overDueReason?: OverDueReason | null; twiceReason?: string };

/** The amount a Verify on this row would send: the draft, else what is already entered, else nothing. */
export const paidToSend = (r: CashRowView, d: Draft | undefined): string | null => {
  if (d && d.paid !== undefined) return d.paid;
  if (r.paid !== null && r.paid !== undefined) return r.enteredAs === "BLANK" ? "" : r.paid;
  return null;
};

export const isOverDue = (paid: string | null, netDue: string | null): boolean => paid !== null && paid !== "" && netDue !== null && num(paid) > num(netDue);

/** 0 ≤ paid ≤ 1.5 × due (CashRules.paidRange). netDue 0 allows more only as an advance. */
export const inRange = (paid: string | null, netDue: string | null, reason?: OverDueReason | null): boolean => {
  if (paid === null || paid === "" || netDue === null) return true;
  const p = num(paid);
  const due = num(netDue);
  if (p < 0) return false;
  if (due <= 0) return p <= 0 || reason === "ADVANCE";
  return p <= due * 1.5 + 1e-9;
};

export type VerifyBlock =
  | null
  | "VERIFIED"
  | "OWN"
  | "NOTHING_ENTERED"
  | "GAP"
  | "OVER_DUE_REASON"
  | "TWICE_REASON"
  | "RANGE";

/**
 * Why a single Verify on this row is not offered (null = it is). While the gap is not ₹0 only rows that
 * differ from the due (or are blank) may be verified, to narrow it (7.4 step 4). A page with no photo
 * (an office print, or a page never sent) may still be verified one row at a time from the paper, with
 * photoSha256 null, as the server allows (BE-1 deviation 10); the screen asks first, and group verify
 * still needs the photo.
 */
export const verifyBlock = (r: CashRowView, d: Draft | undefined, check: PrintCheck): VerifyBlock => {
  if (r.verifyState === "VERIFIED") return "VERIFIED";
  if (isOwn(r) || r.canVerify === "NOT_ALLOWED") return "OWN";
  const paid = paidToSend(r, d);
  if (paid === null) return "NOTHING_ENTERED";
  const due = r.netDue;
  const reason = d?.overDueReason ?? r.overDueReason;
  if (!inRange(paid, due, reason)) return "RANGE";
  if (isOverDue(paid, due) && !reason) return "OVER_DUE_REASON";
  if (r.doubts.includes("PAID_TWICE") && !(d?.twiceReason ?? r.twiceReason ?? "").trim()) return "TWICE_REASON";
  if (!gapCleared(check) && due !== null && paid !== "" && num(paid) === num(due)) return "GAP";
  return null;
};

/** Rows a "Verify these n" may take: clean, unverified, not own, not blind, entered as due or untouched. */
export const groupRows = (check: PrintCheck, drafts: Record<string, Draft>, shaOf: (r: CashRowView) => string | null): CashRowView[] => {
  if (!gapCleared(check)) return [];
  return sectionsOf(check)
    .clean.filter((r) => {
      const d = drafts[r.id];
      if (r.verifyState === "VERIFIED" || !r.canGroup || r.canVerify !== "OK" || r.blind || r.netDue === null) return false;
      if (r.doubts.length || r.editCount > 0) return false;
      if (d && d.paid !== undefined && num(d.paid) !== num(r.netDue)) return false;
      if (d && d.paid === "") return false;
      if (r.paid !== null && (r.enteredAs === "BLANK" || num(r.paid) !== num(r.netDue))) return false;
      return !!shaOf(r);
    })
    .slice(0, GROUP_MAX);
};

/** The latest photo of a print page (the one a verify must name, 7.4 step 4). */
export const latestPhotoOfPage = (photos: PhotoView[], page: number): PhotoView | null =>
  photos
    .filter((p) => p.kind === "PAY_SHEET" && p.page === page)
    .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : a.uploadedAt > b.uploadedAt ? -1 : 0))[0] || null;

/** Row number within its page (1-based); prints fill every page with rowsPerPage rows except the last. */
export const rowOnPage = (rowIndex: number, page: number, rowsPerPage: number = PRINT_GEOMETRY.rowsPerPage): number =>
  Math.max(1, rowIndex - (Math.max(1, page) - 1) * rowsPerPage);

/** Row numbers on a page, for "Page 1 of 3 · rows 1–15". */
export const pageRowRange = (rows: Pick<CashRowView, "rowIndex" | "page">[], page: number): [number, number] | null => {
  const idx = rows.filter((r) => r.page === page).map((r) => r.rowIndex);
  return idx.length ? [Math.min(...idx), Math.max(...idx)] : null;
};

/** The band of a row on its page photo, as fractions of the page height (D31). */
export const bandOf = (rowIndex: number, page: number, rowsPerPage: number = PRINT_GEOMETRY.rowsPerPage) =>
  rowBand(rowOnPage(rowIndex, page, rowsPerPage), rowsPerPage);

/** The request item for one row. */
export const rowItem = (r: CashRowView, paid: string, verify: boolean, d: Draft | undefined, sha: string | null, onScreenMs: number | null): RowItem => ({
  rowId: r.id,
  version: r.version,
  paid,
  blind: r.blind,
  verify,
  photoSha256: verify ? sha : null,
  overDueReason: isOverDue(paid, r.netDue) ? d?.overDueReason ?? r.overDueReason ?? null : null,
  twiceReason: r.doubts.includes("PAID_TWICE") ? (d?.twiceReason ?? r.twiceReason ?? "").trim() || null : null,
  onScreenMs,
});

export type KeyAction = { kind: "due" } | { kind: "move"; by: 1 | -1 } | { kind: "flag" } | { kind: "type"; digit: string } | null;

/** Laptop keys on the rows list (11.6 L3): Enter = paid as due, ↑↓ move, F = flag, digits = type. */
export const keyAction = (key: string, inField: boolean): KeyAction => {
  if (key === "ArrowDown") return { kind: "move", by: 1 };
  if (key === "ArrowUp") return { kind: "move", by: -1 };
  if (key === "Enter") return { kind: "due" };
  if (inField) return null;
  if (key === "f" || key === "F") return { kind: "flag" };
  if (/^\d$/.test(key)) return { kind: "type", digit: key };
  return null;
};

export type Correction = { rowIndex: number; personId: string | null; name: string; due: number; paid: number; delta: number; overDueReason: OverDueReason | null };

/** Rows whose entered amount differs from the printed due (the done screen, L4). delta = due − paid. */
export const correctionsOf = (rows: CashRowView[]): Correction[] =>
  rows
    .filter((r) => r.paid !== null && r.netDue !== null && num(r.paid) !== num(r.netDue))
    .map((r) => ({ rowIndex: r.rowIndex, personId: r.personId || null, name: r.name, due: num(r.netDue), paid: num(r.paid), delta: Math.round((num(r.netDue) - num(r.paid)) * 100) / 100, overDueReason: r.overDueReason }))
    .sort((a, b) => a.rowIndex - b.rowIndex);
